import { cn } from "@/lib/utils";
import { getToolIcon } from "@/lib/data/tools";
import { isToolIconUrl, resolveToolIconUrl } from "@/lib/api/tools";

interface ToolIconProps {
  category: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

/** Category glyph, stroked in the current text color (accent by default). */
export function ToolIcon({
  category,
  size = 22,
  strokeWidth = 1.7,
  className,
}: ToolIconProps) {
  const paths = getToolIcon(category);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("text-accent", className)}
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

type TileSize = "sm" | "md" | "lg";

const TILE: Record<
  TileSize,
  { box: string; radius: string; icon: number; emoji: string }
> = {
  sm: { box: "h-[34px] w-[34px]", radius: "rounded-[11px]", icon: 17, emoji: "text-[16px]" },
  md: { box: "h-10 w-10", radius: "rounded-xl", icon: 22, emoji: "text-[20px]" },
  lg: { box: "h-[46px] w-[46px]", radius: "rounded-[14px]", icon: 22, emoji: "text-[24px]" },
};

/** Accent-soft rounded tile: API icon (image/emoji) or category SVG fallback. */
export function IconTile({
  category,
  icon,
  size = "md",
  className,
}: {
  category: string;
  /** From GET /v1/tool `icon` — image URL or emoji. */
  icon?: string;
  size?: TileSize;
  className?: string;
}) {
  const t = TILE[size];
  const trimmed = icon?.trim();
  const showUrl = trimmed ? isToolIconUrl(trimmed) : false;

  return (
    <div
      className={cn(
        "flex flex-none items-center justify-center bg-accent-soft",
        t.box,
        t.radius,
        className,
      )}
    >
      {showUrl && trimmed ? (
        <img
          src={resolveToolIconUrl(trimmed)}
          alt=""
          className="h-[62%] w-[62%] object-contain"
        />
      ) : trimmed ? (
        <span className={cn("leading-none", t.emoji)} aria-hidden="true">
          {trimmed}
        </span>
      ) : (
        <ToolIcon category={category} size={t.icon} />
      )}
    </div>
  );
}
